import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../config/api.config';
import IFSCVerification from './IFSCVerification';

const DIRECTOR_ONLY_CATEGORIES = [
    { value: 'DIRECTORS', label: 'Directors' },
    { value: 'FOUNDER_DIRECTORS', label: 'Founder Directors' },
    { value: 'INDEPENDENT_DIRECTORS', label: 'Independent Directors' },
    { value: 'BOARD_OBSERVERS', label: 'Board Observers' },
    { value: 'PROMOTORS', label: 'Promotors' }
];

const CreateStartupModal = ({ isOpen, onClose, onSuccess, initialData = null, isEdit = false, isSelfEdit = false, directorsOnly = false }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const modalContentRef = useRef(null);

    // Scroll to top when step changes
    useEffect(() => {
        if (modalContentRef.current) {
            modalContentRef.current.scrollTop = 0;
        }
    }, [currentStep]);
    const [formData, setFormData] = useState({
        // Contact Person
        name: '',
        email: '',
        password: '',
        phoneNumber: '',

        // Company Information
        companyName: '',
        about: '',
        website: '',
        industry: '',
        stage: '',
        foundedYear: '',
        valuations: '',

        // Registration Details
        cin: '',
        gstNumber: '',
        panNumber: '',
        dpiitNumber: '',

        // Bank Details (multiple accounts)
        bankAccounts: [
            { accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }
        ],

        // Location
        officialAddress: '',
        branchAddress: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',

        // Logo
        logo: null,
        logoPreview: '',

        // Directors (array)
        directors: [
            { name: '', designation: '', email: '', phone: '', shareholding: '', category: '', panNumber: '', aadharNumber: '' }
        ]
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [industryOptions, setIndustryOptions] = useState([]);
    const [stageOptions, setStageOptions] = useState([]);

    useEffect(() => {
        fetchSettings();
        if (isOpen && initialData && isEdit) {
            // Parse description if it's a JSON string
            let additionalData = {};
            try {
                if (initialData.description) {
                    additionalData = typeof initialData.description === 'string'
                        ? JSON.parse(initialData.description)
                        : initialData.description || {};
                }
            } catch (e) {
                console.error("Failed to parse description", e);
            }

            // Location fields can come from:
            // 1. Direct fields on initialData (from new GET endpoint that includes Startup entity data)
            // 2. From additionalData (description JSON) for backward compatibility
            // Priority: direct fields first, then fallback to description JSON
            const officialAddress = initialData.officialAddress || additionalData.officialAddress || '';
            const branchAddress = initialData.branchAddress || additionalData.branchAddress || '';
            const city = initialData.city || additionalData.city || '';
            const state = initialData.state || additionalData.state || '';
            const country = initialData.country || additionalData.country || 'India';
            const website = initialData.website || additionalData.website || '';
            // Note: initialData.description is the JSON string, not the "about" field
            // The "about" field is inside the parsed additionalData
            const about = additionalData.about || '';

            setFormData({
                name: initialData.name || '',
                email: initialData.email || '',
                password: '', // Don't pre-fill password
                phoneNumber: initialData.phoneNumber || '',
                companyName: initialData.organizationName || '', // organizationName maps to companyName
                about: about,
                website: website,
                industry: initialData.department || '', // department maps to industry
                stage: initialData.jobTitle || '', // jobTitle maps to stage
                foundedYear: additionalData.foundedYear || '',
                valuations: initialData.valuations || additionalData.valuations || '',
                cin: additionalData.cin || '',
                gstNumber: additionalData.gstNumber || '',
                panNumber: additionalData.panNumber || '',
                dpiitNumber: additionalData.dpiitNumber || '',
                officialAddress: officialAddress,
                branchAddress: branchAddress,
                city: city,
                state: state,
                pincode: additionalData.pincode || '',
                country: country,
                logo: null,
                logoPreview: '', // Todo: handle existing logo
                directors: (additionalData.directors && additionalData.directors.length > 0)
                    ? additionalData.directors.map(dir => ({
                        ...dir,
                        category: dir.category || ''
                    }))
                    : [
                        { name: '', designation: '', email: '', phone: '', shareholding: '', category: '' }
                    ],
                bankAccounts: (additionalData.bankAccounts && additionalData.bankAccounts.length > 0)
                    ? additionalData.bankAccounts.map(ba => ({
                        accountHolderName: ba.accountHolderName || '',
                        bankAccountNumber: ba.bankAccountNumber || '',
                        ifscCode: ba.ifscCode || '',
                        bankName: ba.bankName || '',
                        branchName: ba.branchName || ''
                    }))
                    : (additionalData.accountHolderName || additionalData.bankAccountNumber
                        ? [{ accountHolderName: additionalData.accountHolderName || '', bankAccountNumber: additionalData.bankAccountNumber || '', ifscCode: additionalData.ifscCode || '', bankName: additionalData.bankName || '', branchName: additionalData.branchName || '' }]
                        : [{ accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }])
            });
        } else if (isOpen && !isEdit) {
            // Reset form when opening for new startup
            setFormData({
                name: '',
                email: '',
                password: '',
                phoneNumber: '',
                companyName: '',
                about: '',
                website: '',
                industry: '',
                stage: '',
                foundedYear: '',
                valuations: '',
                cin: '',
                gstNumber: '',
                panNumber: '',
                dpiitNumber: '',
                officialAddress: '',
                branchAddress: '',
                city: '',
                state: '',
                pincode: '',
                country: 'India',
                logo: null,
                logoPreview: '',
                directors: [
                    { name: '', designation: '', email: '', phone: '', shareholding: '', category: '' }
                ],
                bankAccounts: [
                    { accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }
                ]
            });
            setCurrentStep(1);
        }
    }, [isOpen, initialData, isEdit]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            return;
        }
        if (directorsOnly && isEdit) {
            setCurrentStep(4);
        }
    }, [isOpen, directorsOnly, isEdit]);

    const fetchSettings = async () => {
        try {
            const response = await fetch(getApiUrl('settings/grouped'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();

            if (data.INDUSTRY) {
                setIndustryOptions(data.INDUSTRY);
            }

            if (data.STARTUP_STAGE) {
                setStageOptions(data.STARTUP_STAGE);
            }
        } catch (error) {
            console.error('Failed to fetch settings:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({
                ...formData,
                logo: file,
                logoPreview: URL.createObjectURL(file)
            });
        }
    };

    const handleDirectorChange = (index, field, value) => {
        const newDirectors = [...formData.directors];
        newDirectors[index][field] = value;
        setFormData({ ...formData, directors: newDirectors });
    };

    const addDirector = () => {
        setFormData({
            ...formData,
            directors: [...formData.directors, { name: '', designation: '', email: '', phone: '', shareholding: '', category: '', panNumber: '', aadharNumber: '' }]
        });
    };

    const removeDirector = (index) => {
        const newDirectors = formData.directors.filter((_, i) => i !== index);
        setFormData({ ...formData, directors: newDirectors });
    };

    const addBankAccount = () => {
        setFormData({
            ...formData,
            bankAccounts: [...formData.bankAccounts, { accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }]
        });
    };

    const removeBankAccount = (index) => {
        const next = formData.bankAccounts.filter((_, i) => i !== index);
        setFormData({ ...formData, bankAccounts: next.length ? next : [{ accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }] });
    };

    const handleBankAccountChange = (index, field, value) => {
        const next = [...formData.bankAccounts];
        next[index] = { ...next[index], [field]: value };
        setFormData({ ...formData, bankAccounts: next });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const foundedYearTrim = String(formData.foundedYear ?? '').trim();
            const descriptionObj = {
                about: formData.about,
                website: formData.website,
                valuations: formData.valuations,
                cin: formData.cin,
                gstNumber: formData.gstNumber,
                panNumber: formData.panNumber,
                dpiitNumber: formData.dpiitNumber,
                officialAddress: formData.officialAddress,
                branchAddress: formData.branchAddress,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                country: formData.country,
                directors: formData.directors,
                bankAccounts: formData.bankAccounts
            };
            if (foundedYearTrim) {
                descriptionObj.foundedYear = foundedYearTrim;
            }

            const payload = {
                name: formData.name,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                organizationName: formData.companyName,
                department: formData.industry,
                jobTitle: formData.stage,
                role: 'STARTUP',
                description: JSON.stringify(descriptionObj)
            };

            // Only include password if creating new user
            if (!isEdit && formData.password) {
                payload.password = formData.password;
            }

            let response;
            if (isEdit && isSelfEdit) {
                // Startup editing own profile: use users/me/startup-profile (no email update)
                response = await fetch(getApiUrl('users/me/startup-profile'), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        name: payload.name,
                        phoneNumber: payload.phoneNumber,
                        organizationName: payload.organizationName,
                        department: payload.department,
                        jobTitle: payload.jobTitle,
                        description: payload.description
                    })
                });
            } else if (isEdit) {
                response = await fetch(getApiUrl(`admin/users/${initialData.id}`), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                payload.password = formData.password;
                payload.isActive = true;
                response = await fetch(getApiUrl('admin/users/create'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const fromApi =
                    errorData.message ||
                    (typeof errorData.error === 'string' ? errorData.error : null) ||
                    (errorData.detail && typeof errorData.detail === 'string' ? errorData.detail : null);
                const errorMessage =
                    fromApi || (isEdit ? 'Failed to update startup' : 'Failed to create startup');

                // Check for duplicate email error
                if (errorMessage.includes('duplicate key') || errorMessage.includes('already exists')) {
                    throw new Error(`Email "${formData.email}" is already registered. Please use a different email address.`);
                } else {
                    throw new Error(errorMessage);
                }
            }

            const data = await response.json();
            const savedStartup = (isEdit && isSelfEdit && data.profile) ? data.profile : data;

            // Upload logo if provided
            if (formData.logo) {
                const logoFormData = new FormData();
                logoFormData.append('file', formData.logo);
                logoFormData.append('moduleType', 'STARTUP');
                logoFormData.append('moduleId', savedStartup.id);
                logoFormData.append('fileCategory', 'LOGO');
                logoFormData.append('visibility', 'PUBLIC');

                const logoResponse = await fetch(getApiUrl('files/upload'), {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: logoFormData
                });

                if (!logoResponse.ok) {
                    console.error('Failed to upload logo');
                }
            }

            // Pass the created startup data to onSuccess callback if provided
            if (onSuccess) {
                onSuccess(savedStartup);
            }
            onClose();
            setCurrentStep(1);
            setFormData({
                name: '',
                email: '',
                password: '',
                phoneNumber: '',
                companyName: '',
                about: '',
                website: '',
                industry: '',
                stage: '',
                foundedYear: '',
                valuations: '',
                cin: '',
                gstNumber: '',
                panNumber: '',
                dpiitNumber: '',
                officialAddress: '',
                branchAddress: '',
                city: '',
                state: '',
                pincode: '',
                country: 'India',
                logo: null,
                logoPreview: '',
                directors: [
                    { name: '', designation: '', email: '', phone: '', shareholding: '', category: '' }
                ],
                bankAccounts: [
                    { accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }
                ]
            });
        } catch (err) {
            console.error('Error saving startup:', err);
            setError(err.message || (isEdit ? 'Failed to update startup' : 'Failed to create startup'));
        } finally {
            setLoading(false);
        }
    };

    const maxStep = 4;
    const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, maxStep));
    const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

    if (!isOpen) return null;

    const showWizardChrome = !directorsOnly;

    const getDirectorCategoryOptions = (director) => {
        const fullList = [
            { value: 'PROMOTORS', label: 'Promotors' },
            { value: 'INVESTORS', label: 'Investors' },
            { value: 'DIRECTORS', label: 'Directors' },
            { value: 'BOARD_OBSERVERS', label: 'Board Observers' },
            { value: 'INDEPENDENT_DIRECTORS', label: 'Independent Directors' },
            { value: 'FOUNDER_DIRECTORS', label: 'Founder Directors' }
        ];
        if (!directorsOnly) return fullList;
        const base = [...DIRECTOR_ONLY_CATEGORIES];
        const c = director.category;
        if (c && !base.some((o) => o.value === c)) {
            return [{ value: c, label: String(c).replace(/_/g, ' ') }, ...base];
        }
        return base;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div ref={modalContentRef} className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-xl z-10">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-white">
                                {directorsOnly ? 'Directors' : isEdit ? 'Edit Startup' : 'Create New Startup'}
                            </h2>
                            <p className="text-blue-100 text-sm mt-1">
                                {directorsOnly ? 'Add or update director details' : `Step ${currentStep} of 4`}
                            </p>
                        </div>
                        <button onClick={onClose} className="text-white hover:text-gray-200 transition">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Progress Bar */}
                    {showWizardChrome && (
                        <div className="mt-4 flex space-x-2">
                            {[1, 2, 3, 4].map((step) => (
                                <div
                                    key={step}
                                    className={`flex-1 h-2 rounded-full ${step <= currentStep ? 'bg-white' : 'bg-blue-400'
                                        }`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 mb-6">
                            <div className="flex items-start">
                                <svg className="w-6 h-6 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1">
                                    <h4 className="text-sm font-semibold text-red-800 mb-1">
                                        {isEdit ? 'Error Updating Startup' : 'Error Creating Startup'}
                                    </h4>
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Contact & Company Info */}
                    {!directorsOnly && currentStep === 1 && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Person Details</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            disabled={isEdit}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            disabled={isEdit}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                                            placeholder="john@startup.com"
                                        />
                                    </div>

                                    {!isEdit && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Password <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                required
                                                minLength="6"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Min 6 characters"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            name="phoneNumber"
                                            value={formData.phoneNumber}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="+91 98765 43210"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Startup Inc."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            About Company
                                        </label>
                                        <textarea
                                            name="about"
                                            value={formData.about}
                                            onChange={handleChange}
                                            rows="4"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Brief description of your startup..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Valuations
                                        </label>
                                        <textarea
                                            name="valuations"
                                            value={formData.valuations}
                                            onChange={handleChange}
                                            rows="2"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Current and historical valuations (e.g., Seed: $1M, Series A: $5M)"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Website
                                            </label>
                                            <input
                                                type="url"
                                                name="website"
                                                value={formData.website}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="https://startup.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Founded Year <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="foundedYear"
                                                value={formData.foundedYear}
                                                onChange={handleChange}
                                                required
                                                min="1900"
                                                max={new Date().getFullYear()}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="2024"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Industry <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="industry"
                                                value={formData.industry}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="">Select Industry</option>
                                                {industryOptions.length > 0 ? (
                                                    industryOptions.map((option) => (
                                                        <option key={option.id} value={option.value}>
                                                            {option.value}
                                                        </option>
                                                    ))
                                                ) : (
                                                    // Fallback
                                                    <>
                                                        <option value="Technology">Technology</option>
                                                        <option value="Healthcare">Healthcare</option>
                                                        <option value="Fintech">Fintech</option>
                                                        <option value="E-commerce">E-commerce</option>
                                                        <option value="Education">Education</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Stage <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="stage"
                                                value={formData.stage}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="">Select Stage</option>
                                                {stageOptions.length > 0 ? (
                                                    stageOptions.map((option) => (
                                                        <option key={option.id} value={option.value}>
                                                            {option.value}
                                                        </option>
                                                    ))
                                                ) : (
                                                    // Fallback
                                                    <>
                                                        <option value="Idea">Idea</option>
                                                        <option value="MVP">MVP</option>
                                                        <option value="Seed">Seed</option>
                                                        <option value="Series A">Series A</option>
                                                        <option value="Growth">Growth</option>
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Logo
                                        </label>
                                        <div className="flex items-center space-x-4">
                                            {formData.logoPreview && (
                                                <img
                                                    src={formData.logoPreview}
                                                    alt="Logo preview"
                                                    className="w-20 h-20 object-cover rounded-lg border-2 border-gray-300"
                                                />
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoChange}
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Registration Details */}
                    {!directorsOnly && currentStep === 2 && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Details</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        CIN (Corporate Identification Number)
                                    </label>
                                    <input
                                        type="text"
                                        name="cin"
                                        value={formData.cin}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="U12345AB2024PTC123456"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        GST Number
                                    </label>
                                    <input
                                        type="text"
                                        name="gstNumber"
                                        value={formData.gstNumber}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="22AAAAA0000A1Z5"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        PAN Number
                                    </label>
                                    <input
                                        type="text"
                                        name="panNumber"
                                        value={formData.panNumber}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="AAAAA1234A"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        DPIIT Recognition Number
                                    </label>
                                    <input
                                        type="text"
                                        name="dpiitNumber"
                                        value={formData.dpiitNumber}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="DIPP12345"
                                    />
                                </div>
                            </div>

                            {/* Bank Account Details (multiple) */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-md font-semibold text-gray-900">Bank Account Details</h4>
                                    <button type="button" onClick={addBankAccount} className="text-sm font-medium text-blue-600 hover:text-blue-700">+ Add bank account</button>
                                </div>
                                <div className="space-y-6">
                                    {formData.bankAccounts.map((bank, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4 relative bg-gray-50/50">
                                            {formData.bankAccounts.length > 1 && (
                                                <button type="button" onClick={() => removeBankAccount(index)} className="absolute top-2 right-2 text-red-600 hover:text-red-800 text-sm">Remove</button>
                                            )}
                                            <h5 className="font-medium text-gray-800 mb-3">Bank account {index + 1}</h5>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        value={bank.accountHolderName}
                                                        onChange={(e) => handleBankAccountChange(index, 'accountHolderName', e.target.value)}
                                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="As per bank records"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account Number <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        value={bank.bankAccountNumber}
                                                        onChange={(e) => handleBankAccountChange(index, 'bankAccountNumber', e.target.value)}
                                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Enter bank account number"
                                                    />
                                                </div>
                                                <IFSCVerification
                                                    onVerified={(bankDetails) => {
                                                        setFormData(prev => {
                                                            const next = [...prev.bankAccounts];
                                                            next[index] = { ...next[index], ifscCode: bankDetails.ifscCode, bankName: bankDetails.bankName, branchName: bankDetails.branchName };
                                                            return { ...prev, bankAccounts: next };
                                                        });
                                                    }}
                                                    initialValue={bank.ifscCode}
                                                />
                                                {bank.bankName && (
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                                                            <input type="text" value={bank.bankName} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
                                                            <input type="text" value={bank.branchName} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Location */}
                    {!directorsOnly && currentStep === 3 && (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Location</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Official Address <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="officialAddress"
                                        value={formData.officialAddress}
                                        onChange={handleChange}
                                        required
                                        rows="3"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Registered office address - Street address, building name, floor"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Branch Address
                                    </label>
                                    <textarea
                                        name="branchAddress"
                                        value={formData.branchAddress}
                                        onChange={handleChange}
                                        rows="3"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Branch office address (if different from official address)"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            City <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Bangalore"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            State <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Karnataka"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            PIN Code <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="pincode"
                                            value={formData.pincode}
                                            onChange={handleChange}
                                            required
                                            pattern="[0-9]{6}"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="560001"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Country <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="country"
                                            value={formData.country}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Directors */}
                    {(directorsOnly || currentStep === 4) && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {directorsOnly ? 'Director details' : 'Directors / Founders'}
                                </h3>
                                <button
                                    type="button"
                                    onClick={addDirector}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span>Add Director</span>
                                </button>
                            </div>

                            {formData.directors.map((director, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                                    {formData.directors.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeDirector(index)}
                                            className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}

                                    <h4 className="font-medium text-gray-900 mb-3">Director {index + 1}</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={director.name}
                                                onChange={(e) => handleDirectorChange(index, 'name', e.target.value)}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Director name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Category <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={director.category || ''}
                                                onChange={(e) => handleDirectorChange(index, 'category', e.target.value)}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="">Select Category</option>
                                                {getDirectorCategoryOptions(director).map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Designation <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={director.designation}
                                                onChange={(e) => handleDirectorChange(index, 'designation', e.target.value)}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="CEO, CTO, etc."
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                value={director.email}
                                                onChange={(e) => handleDirectorChange(index, 'email', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="director@startup.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                value={director.phone}
                                                onChange={(e) => handleDirectorChange(index, 'phone', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="+91 98765 43210"
                                            />
                                        </div>

                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Shareholding %
                                            </label>
                                            <input
                                                type="number"
                                                value={director.shareholding}
                                                onChange={(e) => handleDirectorChange(index, 'shareholding', e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="25.5"
                                            />
                                        </div>

                                        {/* PAN Number */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                PAN Number
                                            </label>
                                            <input
                                                type="text"
                                                value={director.panNumber || ''}
                                                onChange={(e) => handleDirectorChange(index, 'panNumber', e.target.value.toUpperCase())}
                                                maxLength="10"
                                                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                                placeholder="ABCDE1234F"
                                                style={{ textTransform: 'uppercase' }}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Format: 5 letters, 4 digits, 1 letter</p>
                                        </div>

                                        {/* Aadhar Number */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Aadhar Number
                                            </label>
                                            <input
                                                type="text"
                                                value={director.aadharNumber || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    handleDirectorChange(index, 'aadharNumber', value);
                                                }}
                                                maxLength="12"
                                                pattern="[0-9]{12}"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="XXXX XXXX XXXX"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">12-digit Aadhar number</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className={`flex pt-6 border-t mt-6 ${directorsOnly ? 'justify-end' : 'justify-between'}`}>
                        {!directorsOnly && (
                            <button
                                type="button"
                                onClick={prevStep}
                                disabled={currentStep === 1}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                        )}

                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>

                            {!directorsOnly && currentStep < 4 ? (
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition"
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span>{directorsOnly ? 'Saving...' : isEdit ? 'Updating...' : 'Creating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span>
                                                {directorsOnly ? 'Save directors' : isEdit ? 'Update Startup' : 'Create Startup'}
                                            </span>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateStartupModal;
